import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { useAuth } from "./AuthContext";
import {
  MenuBook,
  Assignment,
  Chat,
  Psychology, // AI Assistant
  Task, // Assignments
  Campaign, // School Posts
  Event, // Parents Evening
  SportsSoccer, // Book a Club
  Description, // Consent Forms
  Home,
  Menu,
  Logout
} from '@mui/icons-material';
import SchoolPostsTab from "./components/StudentDashboard/SchoolPostsTab";
import ProfilePicture from "./components/ProfilePicture";
import WebSocketChat from "./WebSocketChat";
import ParentChannelChat from "./ParentChannelChat";
import ParentsEveningSystem from "./components/ParentsEveningSystem";
import ParentClubBooking from "./components/ParentClubBooking";
import ParentConsentForms from "./components/ParentConsentForms";
import ParentSchoolPolicies from "./components/ParentSchoolPolicies";
import ParentTimetable from "./components/ParentTimetable";
import StudentResourcesTab from "./components/StudentResourcesTab";
import { buildApiUrl } from "./config";
// Chat removed

const ParentDashboard = () => {
  let parentId = localStorage.getItem("parentEmail");
  const { logout } = useAuth();

  // FOR TESTING: Set a default parent if none is logged in
  if (!parentId) {
    parentId = "m.kn321@yahoo.co.uk"; // Test parent from parents.json
    localStorage.setItem("parentEmail", parentId);
    console.log("⚠️ No parent logged in, using test parent:", parentId);
  }

  console.log("ParentDashboard initialized with parentId:", parentId);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current view from URL parameter, default to "home" (landing page)
  const currentViewFromUrl = searchParams.get('module') || 'home';
  const [currentView, setCurrentView] = useState(currentViewFromUrl);

  const [students, setStudents] = useState([]);
  const [parentName, setParentName] = useState("");
  const [notes, setNotes] = useState({});
  const [resources, setResources] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [unreadPostsCount, setUnreadPostsCount] = useState(0); // ✅ NEW: Track unread posts
  const [unreadConsentCount, setUnreadConsentCount] = useState(0); // ✅ NEW: Track unread consent forms

  // 🔔 Notification state
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [notifByModule, setNotifByModule] = useState({});
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(false);

  const [aiMessage, setAiMessage] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [asking, setAsking] = useState(false);
  const [lessonPrompt, setLessonPrompt] = useState("");
  const [generatedHomework, setGeneratedHomework] = useState("");
  const [assignmentTab, setAssignmentTab] = useState(0);
  const [homework, setHomework] = useState({}); // ✅ CHANGED: Store homework per child as object {school_id: [homework]}
  const [submissions, setSubmissions] = useState({}); // ✅ ADD: Track homework submissions
  const [assignments, setAssignments] = useState({});
  const [staff, setStaff] = useState([]);
  const [selectedChildIdx, setSelectedChildIdx] = useState(0);
  const [postsChildIdx, setPostsChildIdx] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [teacherContacts, setTeacherContacts] = useState([]);
  
  // ✅ Mobile menu state for SchoolPostsTab
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ✅ PREVENT AUTO-SCROLLING in ParentDashboard
  useEffect(() => {
    // Disable auto-scroll behaviors for this component
    document.body.style.scrollBehavior = 'auto';
    document.documentElement.style.scrollBehavior = 'auto';

    return () => {
      // Cleanup if needed
    };
  }, []);

  // 🔔 Fetch unread notification count
  useEffect(() => {
    if (!parentId) return;
    const fetchNotifCount = async () => {
      try {
        const res = await axios.get(buildApiUrl(`/api/notifications/${encodeURIComponent(parentId)}/unread-count`));
        if (res.data.success) {
          setNotifUnreadCount(res.data.total_unread);
          setNotifByModule(res.data.by_module || {});
        }
      } catch (err) {
        console.log('Notification count fetch skipped:', err.message);
      }
    };
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [parentId]);

  // 🔔 Open notification panel
  const openNotifPanel = async () => {
    setShowNotifPanel(!showNotifPanel);
    if (!showNotifPanel) {
      setIsLoadingNotifs(true);
      try {
        const res = await axios.get(buildApiUrl(`/api/notifications/${encodeURIComponent(parentId)}?limit=20`));
        if (res.data.success) {
          setNotifications(res.data.notifications);
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
      } finally {
        setIsLoadingNotifs(false);
      }
    }
  };

  // 🔔 Handle notification click - navigate to module and mark as read
  const handleNotifClick = async (notif) => {
    // Mark this notification as read
    if (!notif.is_read) {
      try {
        await axios.post(buildApiUrl('/api/notifications/mark-read'), {
          notification_ids: [notif.id]
        });
        setNotifUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
    // Navigate to the module
    setShowNotifPanel(false);
    if (notif.module_id) {
      handleViewChange(notif.module_id);
    }
  };

  // 🔔 Mark all notifications as read
  const markAllNotifsRead = async () => {
    try {
      await axios.post(buildApiUrl('/api/notifications/mark-read'), {
        user_id: parentId,
        mark_all: true
      });
      setNotifUnreadCount(0);
      setNotifByModule({});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  // Function to handle view changes
  const handleViewChange = (newView) => {
    setCurrentView(newView);
    // Update URL parameter
    if (newView === 'home') {
      // Remove module parameter for home view
      setSearchParams({});
    } else {
      // Set module parameter for other views
      setSearchParams({ module: newView });
    }
  };

  // Function to go back to home
  const goToHome = () => {
    setCurrentView('home');
    setSearchParams({});
  };

  // Sync currentView with URL changes (browser back/forward)
  useEffect(() => {
    const moduleFromUrl = searchParams.get('module') || 'home';
    if (moduleFromUrl !== currentView) {
      setCurrentView(moduleFromUrl);
    }
  }, [searchParams]);

  // Dashboard modules configuration
  const dashboardModules = [
    {
      id: "resources",
      title: "Learning Resources",
      description: "Access study materials and educational content",
      Icon: MenuBook,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      gradient: "from-blue-500 to-blue-600",
    },
    {
      id: "notes",
      title: "Teacher Notes",
      description: "View teacher notes and feedback",
      Icon: Assignment,
      color: "text-green-600",
      bgColor: "bg-green-100",
      gradient: "from-green-500 to-green-600",
    },
    {
      id: "chat",
      title: "Teacher Chat",
      description: "Message your child's teachers",
      Icon: Chat,
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
      gradient: "from-cyan-500 to-cyan-600",
    },
    {
      id: "assignments",
      title: "Assignments",
      description: "View homework and assignments",
      Icon: Task,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      gradient: "from-orange-500 to-orange-600",
    },
    {
      id: "posts",
      title: "School Posts",
      description: "Stay updated with school announcements",
      Icon: Campaign,
      color: "text-red-600",
      bgColor: "bg-red-100",
      gradient: "from-red-500 to-red-600",
    },
    {
      id: "parents-evening",
      title: "Event Booking",
      description: "Book parents evening, wrap around care & other events",
      Icon: Event,
      color: "text-pink-600",
      bgColor: "bg-pink-100",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      id: "club-booking",
      title: "Book a Club",
      description: "Enroll your child in after school activities",
      Icon: SportsSoccer,
      color: "text-indigo-600",
      bgColor: "bg-indigo-100",
      gradient: "from-indigo-500 to-purple-600",
    },
    {
      id: "consent-forms",
      title: "Consent Forms",
      description: "Complete required consent forms",
      Icon: Description,
      color: "text-teal-600",
      bgColor: "bg-teal-100",
      gradient: "from-teal-500 to-cyan-600",
    },
    {
      id: "school-policies",
      title: "School Policies",
      description: "View school policies and documents",
      Icon: MenuBook,
      color: "text-violet-600",
      bgColor: "bg-violet-100",
      gradient: "from-violet-500 to-purple-600",
    },
    {
      id: "timetable",
      title: "Timetable",
      description: "View your children's class schedules",
      Icon: Event,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      gradient: "from-blue-500 to-indigo-600",
    }
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setHomework({}); // ✅ CHANGED: Clear homework object before loading new data
      try {
        // Get parent object from backend API (school-aware)
        const parentRes = await axios.get(buildApiUrl("/data/parents.json"));
        const parentEmail = parentId;
        const matched = parentRes.data.find(p => p.email === parentEmail);

        // Set parent name
        setParentName(matched?.name || matched?.full_name || "Parent");

        const childIds = matched?.child_ids || [];
        // Get all students - ✅ Use buildApiUrl with endpoint
        const studentRes = await axios.get(buildApiUrl("/synced-students"));
        const allStudents = studentRes.data.students || [];
        const children = allStudents.filter(s => childIds.includes(s.school_id));
        setStudents(children);

        // Load notes/resources for each child
        for (let child of children) {
          // Load Teacher Notes from new PostgreSQL endpoint (only published notes)
          try {
            const notesRes = await axios.get(buildApiUrl(`/api/pupil-notes-public/${child.school_id}`));
            console.log(`📝 Fetched published notes for ${child.school_id}:`, notesRes.data);
            const notesArray = notesRes.data?.notes || [];
            setNotes(prev => ({ ...prev, [child.school_id]: notesArray }));
          } catch (err) {
            console.error(`❌ Error fetching teacher notes for ${child.school_id}:`, err);
            setNotes(prev => ({ ...prev, [child.school_id]: [] }));
          }

          // Resources
          try {
            const resRes = await axios.get(buildApiUrl(`/resources/${child.form}`));
            setResources(prev => ({ ...prev, [child.form]: resRes.data || {} }));
          } catch (err) {
            setResources(prev => ({ ...prev, [child.form]: {} })); // No resources found
          }

          // ✅ Load homework assignments for each child
          try {
            console.log(`🔄 Fetching homework for ${child.full_name} (${child.school_id})`);
            const homeworkRes = await axios.get(buildApiUrl(`/student-homework/${child.school_id}`));
            console.log(`📚 API Response for ${child.full_name}:`, homeworkRes.data);
            console.log(`📚 Homework items found:`, homeworkRes.data.homework?.length || 0);
            // ✅ CHANGED: Store homework per child using school_id as key
            setHomework(prev => ({
              ...prev,
              [child.school_id]: homeworkRes.data.homework || []
            }));
          } catch (err) {
            console.error(`❌ Failed to load homework for ${child.full_name}:`, err);
            console.error(`❌ Error details:`, err.response?.data || err.message);
            // Store empty array for this child on error
            setHomework(prev => ({
              ...prev,
              [child.school_id]: []
            }));
          }
        }

        // Load assignments (teacher->form mapping)
        // Add cache-busting query parameter to force fresh data
        const assignmentsRes = await axios.get(buildApiUrl(`/teacher_assignments.json?t=${Date.now()}`));
        setAssignments(assignmentsRes.data);

        // ✅ Load homework submissions for all children
        try {
          console.log("🔄 Loading homework submissions...");
          const submissionsRes = await axios.get(buildApiUrl('/homework-result-map'));
          console.log("📊 Submissions data loaded:", submissionsRes.data);
          setSubmissions(submissionsRes.data);
        } catch (err) {
          console.error("❌ Failed to load homework submissions:", err);
          setSubmissions({});
        }

        // Load staff
        const staffRes = await axios.get("/staff.json");
        console.log("Raw staff response:", staffRes.data);
        const staffData = staffRes.data.value || staffRes.data;
        console.log("Processed staff data:", staffData);
        console.log("Staff data length:", staffData?.length);
        setStaff(staffData);

        // Debug: log all loaded data
        console.log("Final loaded data:", {
          parentName,
          students: children,
          staff: staffData,
          parentId
        });

        // Load total unread message count
        try {
          const unreadResponse = await axios.get(buildApiUrl(`/messages/unread-count/${parentId}`));
          setTotalUnreadCount(unreadResponse.data?.unread_count || 0);
        } catch (err) {
          console.error("Failed to load unread count:", err);
          setTotalUnreadCount(0);
        }

        // Load unread consent forms count
        try {
          const consentResponse = await axios.get(buildApiUrl(`/api/consent-forms/unread-count/${parentId}`));
          setUnreadConsentCount(consentResponse.data?.unread_count || 0);
        } catch (err) {
          console.error("Failed to load unread consent forms count:", err);
          setUnreadConsentCount(0);
        }

      } catch (err) {
        console.error("Failed to load parent data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [parentId]);

  // Refresh unread count every 5 seconds
  useEffect(() => {
    if (!parentId) return;

    const refreshUnreadCount = async () => {
      try {
        const unreadResponse = await axios.get(buildApiUrl(`/messages/unread-count/${parentId}`));
        setTotalUnreadCount(unreadResponse.data?.unread_count || 0);
      } catch (err) {
        console.error("Failed to refresh unread count:", err);
      }
    };

    const interval = setInterval(refreshUnreadCount, 5000);
    return () => clearInterval(interval);
  }, [parentId]);

  // Refresh unread consent forms count every 5 seconds
  useEffect(() => {
    if (!parentId) return;

    const refreshConsentCount = async () => {
      try {
        const consentResponse = await axios.get(buildApiUrl(`/api/consent-forms/unread-count/${parentId}`));
        setUnreadConsentCount(consentResponse.data?.unread_count || 0);
      } catch (err) {
        console.error("Failed to refresh consent forms count:", err);
      }
    };

    const interval = setInterval(refreshConsentCount, 5000);
    return () => clearInterval(interval);
  }, [parentId]);

  const currentChild = students[selectedChildIdx];

  // Chat removed

  // ----------- Early returns for loading or no children -----------
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">Loading Parent Portal...</h2>
          <p className="text-gray-600 mt-2">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Children Found</h2>
          <p className="text-gray-600">No assigned children found for this parent account.</p>
        </div>
      </div>
    );
  }

  // Component that displays module with notification badge
  const ModuleWithBadge = ({ module }) => {
    const showChatBadge = module.id === "chat" && totalUnreadCount > 0;
    const showPostsBadge = module.id === "posts" && unreadPostsCount > 0;
    const showConsentBadge = module.id === "consent-forms" && unreadConsentCount > 0;
    const Icon = module.Icon;

    return (
      <div
        onClick={() => handleViewChange(module.id)}
        className="group cursor-pointer flex flex-col items-center"
      >
        {/* Icon circle with gradient background */}
        <div className="relative">
          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${module.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl group-active:scale-95 transition-all duration-200`}>
            <Icon className="text-white" sx={{ fontSize: 30 }} />
          </div>
          {/* Chat badge */}
          {showChatBadge && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center border-2 border-white text-xs font-bold">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </div>
          )}
          {/* Posts badge */}
          {showPostsBadge && (
            <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3 border-2 border-white"></div>
          )}
          {/* Consent forms badge */}
          {showConsentBadge && (
            <div className="absolute -top-1 -right-1 bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center border-2 border-white text-xs font-bold">
              {unreadConsentCount > 9 ? '9+' : unreadConsentCount}
            </div>
          )}
        </div>
        {/* Label */}
        <p className="mt-2 text-xs font-semibold text-gray-700 text-center leading-tight max-w-[72px] group-hover:text-gray-900 transition-colors">
          {module.title}
        </p>
      </div>
    );
  };

  // Home Page Component
  const HomePage = () => {
    const [showHamMenu, setShowHamMenu] = useState(false);
    const parentFirstName = parentName?.split(' ')[0] || 'Parent';
    const currentChild = students[selectedChildIdx] || students[0];
    const childFirstName = currentChild?.full_name?.split(' ')[0] || 'your child';

    const childHomework = homework[currentChild?.school_id] || [];

    // Upcoming: not submitted, due today or later
    const upcomingAssignments = childHomework
      .filter(hw => {
        const sub = submissions[currentChild?.school_id]?.[hw.id];
        const due = new Date(hw.dueDate);
        return !sub?.submitted_at && due >= new Date(new Date().setHours(0, 0, 0, 0));
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 1);

    // Recent graded submissions
    const recentGrades = childHomework
      .filter(hw => {
        const sub = submissions[currentChild?.school_id]?.[hw.id];
        return sub?.submitted_at && sub?.score !== undefined;
      })
      .sort((a, b) => {
        const aSub = submissions[currentChild?.school_id]?.[a.id];
        const bSub = submissions[currentChild?.school_id]?.[b.id];
        return new Date(bSub.submitted_at) - new Date(aSub.submitted_at);
      })
      .slice(0, 4);

    const getScoreLabel = (score, total) => {
      if (score === undefined || !total) return `${score ?? '?'}`;
      const pct = Math.round((score / total) * 100);
      if (pct >= 90) return `A (${pct}%)`;
      if (pct >= 80) return `B+ (${pct}%)`;
      if (pct >= 70) return `B (${pct}%)`;
      if (pct >= 60) return `C (${pct}%)`;
      return `${pct}%`;
    };

    const allModules = [
      { id: "assignments", label: "Assignments", icon: "📝", badge: 0 },
      { id: "notes", label: "Teacher Notes", icon: "📋", badge: 0 },
      { id: "chat", label: "Messages", icon: "💬", badge: totalUnreadCount },
      { id: "posts", label: "Posts", icon: "📢", badge: 0 },
      { id: "parents-evening", label: "Event Booking", icon: "📅", badge: 0 },
      { id: "club-booking", label: "Book a Club", icon: "⚽", badge: 0 },
      { id: "consent-forms", label: "Consent Forms", icon: "📄", badge: unreadConsentCount },
      { id: "timetable", label: "Timetable", icon: "🗓️", badge: 0 },
      { id: "resources", label: "Resources", icon: "📚", badge: 0 },
      { id: "school-policies", label: "School Policies", icon: "📜", badge: 0 },
      { id: "reports", label: "Reports", icon: "📊", badge: 0 },
      { id: "documents", label: "Documents", icon: "📁", badge: 0 },
    ];

    return (
      <>
      <style>{`
        @media (min-width: 768px) {
          .parent-desktop-panel {
            width: 50% !important;
            box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
            border-left: 1px solid #d1d5db;
            border-right: 1px solid #d1d5db;
          }
          .parent-desktop-bg {
            background-color: #9ca3af !important;
          }
        }
      `}</style>
      <div className="parent-desktop-bg min-h-screen bg-gray-100" style={{display:'flex', justifyContent:'center'}}>
      <div className="parent-desktop-panel w-full min-h-screen bg-gray-100 pb-24 md:pb-8 relative">

        {/* ── Dark Navy Hero ── */}
        <div className="relative bg-gradient-to-br from-[#0f2752] via-[#1a3a72] to-[#0d2444] overflow-hidden pb-12 pt-2 px-5">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
          <div className="absolute top-20 -right-6 w-28 h-28 rounded-full bg-white/5 pointer-events-none" />

          {/* Top bar */}
          <div className="relative z-10 flex items-center justify-between pt-4 mb-5">
            {/* Parent avatar + name */}
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-sm border-2 border-white/40 shadow-lg flex-shrink-0">
                {parentName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'P'}
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">{parentName || 'Parent'}</p>
                <p className="text-blue-300 text-xs">Parent Account</p>
              </div>
            </div>

            {/* Bell + Settings */}
            <div className="flex items-center gap-2">
              <button
                onClick={openNotifPanel}
                className="relative p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                title="Notifications"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#0f2752]" />
                )}
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowHamMenu(!showHamMenu)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                  title="Settings"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                {showHamMenu && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-1 min-w-[140px] z-50">
                    <button
                      onClick={() => { setShowHamMenu(false); logout(); navigate('/'); }}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium"
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Welcome heading */}
          <div className="relative z-10">
            <h1 className="text-white text-2xl sm:text-3xl font-bold leading-snug">
              Hello {parentFirstName},<br />How's {childFirstName} doing today?
            </h1>
            <p className="text-blue-300 text-sm mt-1">Keep up the great work!</p>
          </div>
        </div>

        {/* ── Cards area ── */}
        <div className="relative -mt-4 px-4 space-y-4">

          {/* Child selector + Progress Overview card */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-[#1e3a6e] px-4 py-3">
              {students.length > 1 ? (
                <div className="flex items-center gap-2 overflow-x-auto">
                  {students.map((s, idx) => (
                    <button
                      key={s.school_id}
                      onClick={() => setSelectedChildIdx(idx)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors ${
                        idx === selectedChildIdx ? 'bg-white text-[#1e3a6e]' : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      <img
                        src={buildApiUrl(`student-photo/${s.school_id}`)}
                        alt={s.full_name}
                        className="w-5 h-5 rounded-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {s.full_name.split(' ')[0]}
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <p className="text-white font-bold text-sm">{currentChild?.full_name}'s Progress Overview</p>
                  <p className="text-blue-200 text-xs">{currentChild?.form ? `Form ${currentChild.form}` : ''}</p>
                </div>
              )}
            </div>
            <div className="px-4 py-4">
              {students.length > 1 && (
                <div className="mb-3">
                  <p className="text-sm font-bold text-gray-800">{currentChild?.full_name}'s Progress Overview</p>
                  <p className="text-xs text-gray-500">{currentChild?.form ? `Form ${currentChild.form}` : ''}</p>
                </div>
              )}
              {upcomingAssignments.length > 0 ? (
                <div
                  onClick={() => handleViewChange('assignments')}
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-xl p-2 -mx-2 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">📝</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Upcoming Assignment</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{upcomingAssignments[0].title}</p>
                    <p className="text-xs text-orange-500 font-medium">
                      {(() => {
                        const days = Math.ceil((new Date(upcomingAssignments[0].dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                        return days <= 0 ? 'Due Today' : days === 1 ? 'Due Tomorrow' : `Due in ${days} days`;
                      })()}
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              ) : (
                <div className="flex items-center gap-3 py-1">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">🎉</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">No upcoming assignments</p>
                    <p className="text-sm font-semibold text-green-600">{childFirstName} is all caught up!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Grades */}
          {recentGrades.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Recent Grades</h2>
              <div className="grid grid-cols-2 gap-3">
                {recentGrades.map(hw => {
                  const sub = submissions[currentChild?.school_id]?.[hw.id];
                  return (
                    <div
                      key={hw.id}
                      onClick={() => handleViewChange('assignments')}
                      className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-base">📋</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 truncate">{hw.title}</p>
                        <p className="text-sm font-bold text-gray-800">{getScoreLabel(sub?.score, sub?.total)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Modules — 4 per row */}
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Modules</h2>
            <div className="grid grid-cols-4 gap-3">
              {allModules.map(mod => (
                <button
                  key={mod.id}
                  onClick={() => handleViewChange(mod.id)}
                  className="relative bg-white rounded-xl py-3 flex flex-col items-center gap-1 shadow-sm hover:shadow-md active:scale-95 transition-all"
                >
                  <span className="text-2xl">{mod.icon}</span>
                  <span className="text-xs font-medium text-gray-600 text-center leading-tight px-1">{mod.label}</span>
                  {mod.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold border-2 border-gray-100">
                      {mod.badge > 9 ? '9+' : mod.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
      </div>
      </>
    );
  };

  // Individual Module Components
  const ResourcesView = () => {

    // Always initialize with 0 to avoid build errors
    const [localSelectedChildIdx, setLocalSelectedChildIdx] = useState(0);
    const currentChild = students[localSelectedChildIdx];
    const handleChildSelect = (idx) => setLocalSelectedChildIdx(idx);

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header with Home Button */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Back to Home"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <div className="flex items-center">
                  <h1 className="text-3xl">📚</h1>
                </div>
              </div>

              {/* Child Selector */}
              <div className="flex items-center space-x-4">
                <CompactChildSelector
                  onSelectChild={handleChildSelect}
                  currentChildId={currentChild?.school_id}
                />
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Home"
                >
                  <span className="text-xl">🏠</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {currentChild ? (
            <StudentResourcesTab studentForm={currentChild.form} />
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Child</h3>
              <p className="text-gray-600">Please select a child to view their resources</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const NotesView = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">📝 Teacher Notes</h1>
                <p className="text-gray-600">Feedback from teachers for {currentChild.full_name}</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Child Selector with Photos - Show if multiple children */}
      {students.length > 1 && (
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Select Child to View Notes:</h3>
            <div className="flex flex-wrap gap-4">
              {students.map((student, idx) => {
                const isSelected = student.school_id === currentChild.school_id;
                const server = buildApiUrl("");
                const initials = student.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

                return (
                  <button
                    key={student.school_id}
                    onClick={() => setSelectedChildIdx(idx)}
                    className={`flex flex-col items-center p-3 rounded-lg transition-all duration-200 ${
                      isSelected
                        ? 'bg-blue-100 ring-2 ring-blue-500 shadow-md'
                        : 'bg-gray-100 hover:bg-gray-200 ring-1 ring-gray-300'
                    }`}
                  >
                    <div className="relative w-16 h-16 mb-2">
                      <img
                        src={`${server}/student-photo/${student.school_id}?v=3`}
                        alt={student.full_name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div
                        className={`absolute inset-0 hidden rounded-full items-center justify-center text-white text-sm font-bold ${
                          student.full_name.charCodeAt(0) % 8 === 0 ? 'bg-blue-500' :
                          student.full_name.charCodeAt(0) % 8 === 1 ? 'bg-green-500' :
                          student.full_name.charCodeAt(0) % 8 === 2 ? 'bg-purple-500' :
                          student.full_name.charCodeAt(0) % 8 === 3 ? 'bg-pink-500' :
                          student.full_name.charCodeAt(0) % 8 === 4 ? 'bg-yellow-500' :
                          student.full_name.charCodeAt(0) % 8 === 5 ? 'bg-indigo-500' :
                          student.full_name.charCodeAt(0) % 8 === 6 ? 'bg-red-500' :
                          'bg-cyan-500'
                        }`}
                      >
                        {initials}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 text-center">{student.full_name.split(' ')[0]}</span>
                    <span className="text-xs text-gray-600">{student.form}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notes[currentChild.school_id]?.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Teacher</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date & Time</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {notes[currentChild.school_id].map((n, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{n.teacher_name || n.teacher_email || 'Unknown Teacher'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {n.created_at ? new Date(n.created_at).toLocaleDateString() + ' at ' + new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md whitespace-pre-wrap">{n.note_text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Teacher Notes Yet</h3>
            <p className="text-gray-600">Teacher notes and feedback will appear here when teachers add them</p>
          </div>
        )}
      </div>
    </div>
  );

  const AIAssistantView = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🤖 AI Assistant</h1>
                <p className="text-gray-600">Get help with {currentChild.full_name}'s lessons</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ask a question about the lesson...
            </label>
            <textarea
              value={aiMessage}
              onChange={e => setAiMessage(e.target.value)}
              disabled={asking}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
              placeholder="Type your question here..."
            />
          </div>

          <button
            onClick={async () => {
              setAsking(true);
              setAiResponse("");
              try {
                const response = await axios.post(buildApiUrl("/api/ai-assistant"), {
                  message: aiMessage,
                  lessonPrompt,
                  homework: generatedHomework
                });
                setAiResponse(response.data.reply);
              } catch (err) {
                console.error("AI request failed:", err);
                setAiResponse("Error: " + err.message);
              }
              setAsking(false);
            }}
            disabled={asking || !aiMessage.trim()}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {asking ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Thinking...</span>
              </>
            ) : (
              <>
                <span>🤖</span>
                <span>Ask AI</span>
              </>
            )}
          </button>

          {aiResponse && (
            <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
              <div className="flex items-center space-x-2 mb-3">
                <span className="text-2xl">🤖</span>
                <h3 className="font-semibold text-gray-900">AI Response:</h3>
              </div>
              <div className="prose max-w-none text-gray-700">
                <ReactMarkdown>{aiResponse}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Compact Child Selector for Top Bar
  const CompactChildSelector = ({ onSelectChild, currentChildId }) => {
    const server = buildApiUrl("");

    const getAvatarColor = (name) => {
      const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-purple-500',
        'bg-pink-500',
        'bg-yellow-500',
        'bg-indigo-500',
        'bg-red-500',
        'bg-cyan-500'
      ];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    };

    return (
      <div className="flex items-center space-x-2">
        <div className="flex space-x-2">
          {students.map((student, idx) => {
            const isSelected = student.school_id === currentChildId;
            const firstName = student.full_name.split(' ')[0];
            const initials = student.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

            return (
              <button
                key={student.school_id}
                onClick={() => onSelectChild(idx)}
                className={`relative rounded-lg transition-all duration-200 text-center ${isSelected
                  ? 'ring-2 ring-blue-400 ring-offset-2 shadow-md'
                  : 'hover:ring-2 hover:ring-blue-300 hover:ring-offset-2'
                  }`}
                title={`${student.full_name} (${student.form})`}
              >
                <div className="flex flex-col items-center">
                  <div className="relative w-10 h-10">
                    <img
                      src={`${server}/student-photo/${student.school_id}?v=3`}
                      alt={student.full_name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm ${getAvatarColor(student.full_name)} border-2 border-white`}
                      style={{ display: 'none' }}
                    >
                      {initials}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Child Selector Component with Photos
  const CartoonChildSelector = ({ onSelectChild, currentChildId }) => {
    const server = buildApiUrl("");

    const getAvatarColor = (name) => {
      const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-purple-500',
        'bg-pink-500',
        'bg-yellow-500',
        'bg-indigo-500',
        'bg-red-500',
        'bg-cyan-500'
      ];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    };

    return (
      <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200 shadow-sm">
        <div className="text-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Select Child
          </h3>
          <p className="text-sm text-gray-600">
            Choose which child's information to view
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          {students.map((student, idx) => {
            const isSelected = student.school_id === currentChildId;
            const initials = student.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

            return (
              <div
                key={student.school_id}
                onClick={() => onSelectChild(idx)}
                className={`group cursor-pointer transform transition-all duration-200 hover:scale-105 ${isSelected ? 'scale-105' : ''
                  }`}
              >
                <div className={`relative p-4 rounded-xl border-2 shadow-sm transition-all duration-200 ${isSelected
                  ? 'bg-blue-50 border-blue-500 shadow-md'
                  : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}>

                  {/* Student Photo */}
                  <div className="flex justify-center mb-3">
                    <div className="relative w-20 h-20">
                      <img
                        src={`${server}/student-photo/${student.school_id}?v=3`}
                        alt={student.full_name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-white shadow-md"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md ${getAvatarColor(student.full_name)} border-3 border-white`}
                        style={{ display: 'none' }}
                      >
                        {initials}
                      </div>
                    </div>
                  </div>

                  {/* Child info */}
                  <div className="text-center">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {student.full_name.split(' ')[0]}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {student.form}
                    </p>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="mt-2 px-2 py-1 bg-blue-500 rounded-full text-white text-xs font-medium">
                        Selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const AssignmentsView = () => {

    // Always initialize with 0 to avoid build errors
    const [localSelectedChildIdx, setLocalSelectedChildIdx] = useState(0);
    const currentChild = students[localSelectedChildIdx];
    const handleChildSelect = (idx) => setLocalSelectedChildIdx(idx);

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <div className="flex items-center">
                  <h1 className="text-3xl">📝</h1>
                </div>
              </div>

              {/* Compact Child Selector */}
              <div className="flex items-center space-x-4">
                <CompactChildSelector
                  onSelectChild={handleChildSelect}
                  currentChildId={currentChild?.school_id}
                />
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Home"
                >
                  <span className="text-xl">🏠</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {(() => {
            const currentChildHomework = homework[currentChild?.school_id] || [];

            return currentChildHomework.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assignments Yet</h3>
                <p className="text-gray-600">Homework and assignments will appear here</p>
              </div>
            ) : (
              <>
                {/* ===== DESKTOP TABLE VIEW (hidden on small screens) ===== */}
                <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignment</th>
                        <th className="px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Result</th>
                        <th className="px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Files</th>
                        <th className="px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentChildHomework.map(hw => {
                        const submission = submissions[currentChild?.school_id]?.[hw.id];
                        const isSubmitted = !!submission?.submitted_at;
                        const today = new Date(); today.setHours(0,0,0,0);
                        const dueDate = hw.dueDate ? new Date(hw.dueDate) : null;
                        if (dueDate) dueDate.setHours(0,0,0,0);
                        const isOverdue = dueDate && dueDate < today && !isSubmitted;

                        return (
                          <tr key={hw.id} className="hover:bg-gray-50 transition-colors">
                            {/* Title + description */}
                            <td className="px-6 py-4 max-w-xs">
                              <div className="font-semibold text-gray-900 truncate">{hw.title}</div>
                              {hw.content && <div className="text-sm text-gray-500 truncate mt-0.5">{hw.content}</div>}
                            </td>
                            {/* Status badge */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${isSubmitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {isSubmitted ? '✅ Submitted' : '⏳ Pending'}
                              </span>
                            </td>
                            {/* Due date */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              {hw.dueDate ? (
                                <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : isSubmitted ? 'text-gray-400' : 'text-gray-700'}`}>
                                  {isOverdue && '⚠ '}{hw.dueDate}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                            {/* Score / result */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              {isSubmitted ? (
                                <div className="text-sm">
                                  {submission.score !== undefined ? (
                                    <span className="font-semibold text-green-700">{submission.score}/{submission.total || '?'}</span>
                                  ) : (
                                    <span className="text-gray-500 italic">Awaiting grade</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                            {/* Attachments count */}
                            <td className="px-4 py-4 text-center whitespace-nowrap">
                              {hw.attachments?.length > 0 ? (
                                <span className="inline-flex items-center text-sm text-blue-600 font-medium">
                                  📎 {hw.attachments.length}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                            {/* Action button */}
                            <td className="px-4 py-4 text-right whitespace-nowrap">
                              {(() => {
                                const isUploaded = hw.attachments?.length > 0 && (!hw.questions || hw.questions.length === 0);
                                if (isSubmitted) {
                                  return (
                                    <button
                                      onClick={() => window.open(`/homework-result/${currentChild?.school_id}/${hw.id}`, '_blank')}
                                      className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-green-600 hover:bg-green-700 text-white"
                                    >
                                      View Result
                                    </button>
                                  );
                                }
                                if (isUploaded) {
                                  return (
                                    <div className="flex items-center justify-end space-x-2">
                                      {hw.attachments.map((file, i) => (
                                        <a
                                          key={i}
                                          href={file.startsWith('http') ? file : buildApiUrl(file)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                                        >
                                          📎 {hw.attachments.length === 1 ? 'View Assignment' : `File ${i + 1}`}
                                        </a>
                                      ))}
                                    </div>
                                  );
                                }
                                return (
                                  <button
                                    onClick={() => window.open(`/view-homework/${hw.id}`, '_blank')}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    View
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ===== MOBILE CARD-TABLE VIEW (visible only on small screens) ===== */}
                <div className="md:hidden space-y-3">
                  {currentChildHomework.map(hw => {
                    const submission = submissions[currentChild?.school_id]?.[hw.id];
                    const isSubmitted = !!submission?.submitted_at;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const dueDate = hw.dueDate ? new Date(hw.dueDate) : null;
                    if (dueDate) dueDate.setHours(0,0,0,0);
                    const isOverdue = dueDate && dueDate < today && !isSubmitted;

                    return (
                      <div key={hw.id} className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
                        {/* Top row: title + status */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                          <h3 className="font-semibold text-gray-900 text-sm truncate flex-1 mr-2">{hw.title}</h3>
                          <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${isSubmitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {isSubmitted ? '✅ Done' : '⏳ Pending'}
                          </span>
                        </div>

                        {/* Info rows */}
                        <div className="divide-y divide-gray-50 text-sm">
                          {hw.content && (
                            <div className="px-4 py-2 text-gray-600 line-clamp-2">{hw.content}</div>
                          )}

                          <div className="grid grid-cols-2 gap-px bg-gray-100">
                            {/* Due date cell */}
                            <div className="bg-white px-4 py-2.5">
                              <div className="text-xs text-gray-400 uppercase font-medium mb-0.5">Due</div>
                              {hw.dueDate ? (
                                <div className={`font-medium ${isOverdue ? 'text-red-600' : isSubmitted ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {isOverdue && '⚠ '}{hw.dueDate}
                                </div>
                              ) : (
                                <div className="text-gray-400">—</div>
                              )}
                            </div>
                            {/* Result cell */}
                            <div className="bg-white px-4 py-2.5">
                              <div className="text-xs text-gray-400 uppercase font-medium mb-0.5">Result</div>
                              {isSubmitted ? (
                                submission.score !== undefined ? (
                                  <div className="font-semibold text-green-700">{submission.score}/{submission.total || '?'}</div>
                                ) : (
                                  <div className="text-gray-500 italic">Awaiting</div>
                                )
                              ) : (
                                <div className="text-gray-400">—</div>
                              )}
                            </div>
                          </div>

                          {/* Attachments row (only if there are any) */}
                          {hw.attachments?.length > 0 && (
                            <div className="px-4 py-2 flex items-center space-x-1 text-blue-600">
                              <span>📎</span>
                              <span className="font-medium">{hw.attachments.length} attachment{hw.attachments.length > 1 ? 's' : ''}</span>
                            </div>
                          )}

                          {/* Submitted-on row */}
                          {isSubmitted && (
                            <div className="px-4 py-2 text-xs text-green-700 bg-green-50 flex items-center space-x-1">
                              <span>✅</span>
                              <span>Submitted {new Date(submission.submitted_at).toLocaleDateString()} at {new Date(submission.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          )}
                        </div>

                        {/* Action button */}
                        <div className="px-4 py-3 border-t border-gray-100">
                          {(() => {
                            const isUploaded = hw.attachments?.length > 0 && (!hw.questions || hw.questions.length === 0);
                            if (isSubmitted) {
                              return (
                                <button
                                  onClick={() => window.open(`/homework-result/${currentChild?.school_id}/${hw.id}`, '_blank')}
                                  className="w-full text-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-green-600 hover:bg-green-700 text-white"
                                >
                                  View Submitted Homework
                                </button>
                              );
                            }
                            if (isUploaded) {
                              return (
                                <div className="space-y-2">
                                  {hw.attachments.map((file, i) => (
                                    <a
                                      key={i}
                                      href={file.startsWith('http') ? file : buildApiUrl(file)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="w-full block text-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      📎 {hw.attachments.length === 1 ? 'View Assignment' : `View File ${i + 1} — ${file.split('/').pop()}`}
                                    </a>
                                  ))}
                                </div>
                              );
                            }
                            return (
                              <button
                                onClick={() => window.open(`/view-homework/${hw.id}`, '_blank')}
                                className="w-full text-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                View Assignment
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    );
  };

  // PostsView inlined into renderCurrentView to prevent SchoolPostsTab from remounting on parent re-renders

  // Chat removed

  // Floating Chat Widget Component
  // Chat removed

  // Helper function to get total unread chat count
  // Chat removed

  // Chat View Component - WhatsApp Style for Parents
  const ChatView = () => {
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [unreadCounts, setUnreadCounts] = useState({});
    const [latestTimestamps, setLatestTimestamps] = useState({});

    // Load unread message counts
    const loadUnreadCounts = async () => {
      try {
        const response = await axios.get(buildApiUrl(`/messages/unread-by-contact/${parentId}`));
        if (response.data?.unread_by_contact) {
          setUnreadCounts(response.data.unread_by_contact);
        }
      } catch (error) {
        console.error("Failed to load unread counts:", error);
      }
    };

    // ✅ NEW: Load unread posts count
    const loadUnreadPostsCount = async () => {
      try {
        if (students && students.length > 0) {
          const studentForm = students[0]?.form; // Get form from first student
          const response = await axios.get(
            buildApiUrl(`/api/posts/unread-count/${parentId}`),
            { params: { user_role: 'parent', student_form: studentForm } }
          );
          if (response.data?.success) {
            setUnreadPostsCount(response.data.unread_count);
          } else {
            setUnreadPostsCount(0);
          }
        }
      } catch (error) {
        console.error("Failed to load unread posts count:", error);
        setUnreadPostsCount(0); // Reset to 0 on error
      }
    };

    // Load latest message timestamps
    const loadLatestTimestamps = async () => {
      try {
        const response = await axios.get(buildApiUrl(`/messages/latest-timestamps/${parentId}`));
        if (response.data?.latest_timestamps) {
          setLatestTimestamps(response.data.latest_timestamps);
        }
      } catch (error) {
        console.error("Failed to load latest timestamps:", error);
      }
    };

    // Mark thread as read when opening a conversation
    const markThreadAsRead = async (studentId, teacherId) => {
      try {
        await axios.post(buildApiUrl(`/messages/mark-thread-read/${studentId}/${parentId}/${teacherId}`));
        // Update local unread counts
        setUnreadCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[`${teacherId}_${studentId}`];
          return newCounts;
        });
      } catch (error) {
        console.error("Failed to mark thread as read:", error);
      }
    };

    // Load unread counts and timestamps when component mounts
    useEffect(() => {
      loadUnreadCounts();
      loadLatestTimestamps();
      loadUnreadPostsCount(); // ✅ NEW: Load unread posts
      // Refresh every 2 seconds for faster unread badge updates
      const interval = setInterval(() => {
        loadUnreadCounts();
        loadLatestTimestamps();
        loadUnreadPostsCount(); // ✅ NEW: Poll for unread posts
      }, 2000);
      return () => clearInterval(interval);
    }, [parentId, students]); // ✅ Add students dependency

    // Create teacher channels grouped by child
    const teacherChannels = useMemo(() => {
      const channels = [];

      console.log("Creating teacher channels:", {
        studentsCount: students?.length,
        assignmentsKeys: assignments ? Object.keys(assignments) : 'no assignments',
        staffCount: staff?.length,
        parentId,
        parentName
      });

      // Debug: log the data we have
      console.log("Students:", students);
      console.log("Staff:", staff);
      console.log("Assignments:", assignments);

      if (!students || !staff || !assignments) {
        console.log("Missing required data - returning empty channels");
        return [];
      }

      // Get all unique teacher emails from assignments
      const allAssignedEmails = new Set();
      Object.values(assignments || {}).forEach(classData => {
        // Handle new owners/teachers structure
        if (classData && typeof classData === 'object') {
          // Add owners if they exist
          if (Array.isArray(classData.owners)) {
            classData.owners.forEach(email => allAssignedEmails.add(email));
          }
          // Add teachers if they exist
          if (Array.isArray(classData.teachers)) {
            classData.teachers.forEach(email => allAssignedEmails.add(email));
          }
        } else if (Array.isArray(classData)) {
          // Handle old structure (array of emails)
          classData.forEach(email => allAssignedEmails.add(email));
        } else if (typeof classData === 'string') {
          // Handle single email string
          allAssignedEmails.add(classData);
        }
      });

      console.log("All assigned teacher emails:", Array.from(allAssignedEmails));

      // Ensure students is an array before calling forEach
      if (!Array.isArray(students)) {
        console.log("Students is not an array - returning empty channels");
        return [];
      }

      // ✅ NEW: Group students by their teachers first
      const teacherToStudents = new Map();
      
      students.forEach(student => {
        const studentName = student.name || student.full_name || 'Unknown Student';
        if (!student?.school_id || studentName === 'Unknown Student') return;

        // For each assigned teacher email
        Array.from(allAssignedEmails).forEach(teacherEmail => {
          if (!teacherToStudents.has(teacherEmail)) {
            teacherToStudents.set(teacherEmail, []);
          }
          teacherToStudents.get(teacherEmail).push({
            id: student.school_id,
            name: studentName,
            form: student.form
          });
        });
      });

      console.log("Teacher to students mapping:", Array.from(teacherToStudents.entries()));

      // ✅ Create one channel per teacher with all their students listed
      teacherToStudents.forEach((studentsList, teacherEmail) => {
        const teacherDetails = staff.find(t => t.email === teacherEmail);
        console.log(`Creating channel for teacher ${teacherEmail}, found:`, teacherDetails?.name || 'NOT FOUND');

        if (teacherDetails && studentsList.length > 0) {
          // Use the first student as the primary student for the channel
          const primaryStudent = studentsList[0];
          
          // Create subtitle showing all children this teacher teaches
          let subtitle;
          if (studentsList.length === 1) {
            subtitle = `About ${primaryStudent.name} (${primaryStudent.form || 'N/A'})`;
          } else {
            // Show all children's names
            const childNames = studentsList.map(s => s.name).join(', ');
            subtitle = `About ${childNames}`;
          }

          const newChannel = {
            id: `${parentId}_${teacherEmail}_${primaryStudent.id}`, // Use primary student's ID
            teacherId: teacherEmail,
            teacherName: teacherDetails.name || 'Unknown Teacher',
            parentId: parentId,
            parentName: parentName || 'Parent',
            parentEmail: parentId, // parentId is the email
            studentId: primaryStudent.id, // Primary student for message context
            studentName: primaryStudent.name,
            allStudents: studentsList, // ✅ NEW: Keep track of all students this teacher teaches
            channelName: teacherDetails.name || 'Unknown Teacher',
            subtitle: subtitle,
            avatar: (teacherDetails.name?.charAt(0) || 'T')
          };
          channels.push(newChannel);
          console.log("Created channel:", newChannel);
        }
      });

      console.log("Final channels count:", channels.length);
      console.log("Final channels:", channels);
      return channels;
    }, [students, staff, assignments, parentId, parentName]);

    // Filter and sort channels based on search and latest message timestamp
    const searchFilteredChannels = useMemo(() => {
      let filteredChannels = teacherChannels;

      // Apply search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredChannels = teacherChannels.filter(channel =>
          (channel.teacherName || '').toLowerCase().includes(term) ||
          (channel.studentName || '').toLowerCase().includes(term) ||
          (channel.subtitle || '').toLowerCase().includes(term)
        );
      }

      // Sort by latest message timestamp (most recent first)
      return filteredChannels.sort((a, b) => {
        const aKey = `${a.teacherId}_${a.studentId}`;
        const bKey = `${b.teacherId}_${b.studentId}`;
        const aTimestamp = latestTimestamps[aKey] || '1970-01-01T00:00:00';
        const bTimestamp = latestTimestamps[bKey] || '1970-01-01T00:00:00';

        // Sort in descending order (most recent first)
        return new Date(bTimestamp) - new Date(aTimestamp);
      });
    }, [teacherChannels, searchTerm, latestTimestamps]);

    return (
      <div className="bg-gray-50 overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Chat Container - Full Screen WhatsApp Style Layout */}
        <div className="w-full max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
          <div className="flex-1 bg-white shadow-sm overflow-hidden flex flex-col">
            <div className="flex h-full overflow-hidden">

              {/* Left Side - Teacher Contacts (Mobile: Full Width, Desktop: Sidebar) */}
              <div className={`bg-white border-r border-gray-200 flex flex-col ${selectedTeacher ? 'hidden lg:flex lg:w-80 xl:w-96' : 'w-full lg:w-80 xl:w-96'
                }`}>
                {/* Mobile Header with Back Button */}
                <div className="lg:hidden bg-green-600 text-white px-4 py-3 flex items-center gap-3">
                  <button
                    onClick={() => goToHome()}
                    className="p-1 hover:bg-green-700 rounded transition-colors"
                    title="Back to Home"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h2 className="text-lg font-semibold">Teacher Chat</h2>
                </div>

                {/* Desktop Header with Back Button */}
                <div className="hidden lg:flex bg-white border-b border-gray-200 px-4 py-3 items-center gap-3">
                  <button
                    onClick={() => goToHome()}
                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title="Back to Home"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h2 className="text-lg font-semibold text-gray-900">Teacher Chat</h2>
                </div>

                {/* Search Bar */}
                <div className="p-3 lg:p-4 border-b border-gray-200 bg-gray-50 lg:bg-white">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search teachers or students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 lg:py-3 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5 lg:top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Teacher List - Mobile Optimized */}
                <div className="flex-1 overflow-y-auto">
                  {searchFilteredChannels.map((channel, index) => {
                    const unreadKey = `${channel.teacherId}_${channel.studentId}`;
                    const unreadCount = unreadCounts[unreadKey] || 0;
                    const latestTimestamp = latestTimestamps[unreadKey];

                    return (
                      <div
                        key={channel.id}
                        onClick={() => {
                          setSelectedTeacher(channel);
                          if (unreadCount > 0) {
                            markThreadAsRead(channel.studentId, channel.teacherId);
                          }
                        }}
                        className={`flex items-center p-3 lg:p-4 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${selectedTeacher?.id === channel.id ? 'bg-green-50 border-r-4 border-r-green-500' : ''
                          }`}
                      >
                        {/* Avatar */}
                        <div className="w-12 h-12 lg:w-14 lg:h-14 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold text-lg mr-3 flex-shrink-0 relative">
                          {channel.avatar.toUpperCase()}
                          {unreadCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 lg:w-6 lg:h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-xs lg:text-sm text-white font-bold">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Contact Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className={`text-sm lg:text-base font-medium truncate ${unreadCount > 0 ? 'text-gray-900 font-semibold' : 'text-gray-900'
                              }`}>
                              {channel.channelName}
                              {channel.allStudents && channel.allStudents.length === 1 && (
                                <span className="text-xs text-gray-500 font-normal ml-1">
                                  ({channel.allStudents[0].name}'s Teacher)
                                </span>
                              )}
                            </h3>
                            {latestTimestamp && (
                              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                {new Date(latestTimestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs lg:text-sm text-gray-600 truncate mt-1">
                            {channel.subtitle}
                          </p>
                          {/* Mobile: Show unread badge below */}
                          {unreadCount > 0 && (
                            <div className="mt-2 lg:hidden">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {unreadCount} new message{unreadCount > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Desktop unread badge */}
                        <div className="hidden lg:block">
                          {unreadCount > 0 && (
                            <div className="ml-2 px-2 py-1 bg-red-500 text-white rounded-full">
                              <span className="text-xs font-medium">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Mobile arrow indicator */}
                        <svg className="w-5 h-5 text-gray-400 ml-2 lg:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    );
                  })}

                  {searchFilteredChannels.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-4">
                      <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="text-sm text-center">No teachers found</p>
                      <p className="text-xs text-center mt-1">Try adjusting your search</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Chat Area (Mobile: Full Screen, Desktop: Main Area) */}
              <div className={`flex-1 flex flex-col overflow-hidden ${selectedTeacher ? 'flex' : 'hidden lg:flex'
                }`}>
                {selectedTeacher ? (
                  <div className="h-full flex flex-col overflow-hidden">
                    {/* Mobile Chat Header */}
                    <div className="bg-green-600 text-white px-4 py-3 flex items-center lg:hidden flex-shrink-0">
                      <button
                        onClick={() => setSelectedTeacher(null)}
                        className="text-white hover:bg-green-500 rounded-full p-1 mr-3"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                        {selectedTeacher.avatar.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">
                          {selectedTeacher.channelName}
                        </h3>
                        <p className="text-sm text-green-100 truncate">{selectedTeacher.subtitle}</p>
                      </div>
                    </div>

                    {/* Desktop Chat Header */}
                    <div className="hidden lg:flex p-4 border-b bg-gray-50 items-center space-x-3 flex-shrink-0">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {selectedTeacher.avatar.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          {selectedTeacher.channelName}
                        </h3>
                        <p className="text-sm text-gray-500">{selectedTeacher.subtitle}</p>
                      </div>
                    </div>

                    {/* Chat Messages Area */}
                    <div className="flex-1 overflow-hidden min-h-0">
                      <ParentChannelChat
                        parentId={parentId}
                        parentName={parentName}
                        teacherId={selectedTeacher.teacherId}
                        teacherName={selectedTeacher.teacherName}
                        teacherEmail={selectedTeacher.teacherId}
                        studentId={selectedTeacher.studentId}
                        studentName={selectedTeacher.studentName}
                        channelId={selectedTeacher.id}
                      />
                    </div>
                  </div>
                ) : (
                  /* Welcome Message - Hidden on Mobile */
                  <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-8 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Welcome to Teacher Chat</h3>
                    <p className="text-gray-600 mb-4">Select a teacher from the list to start a conversation about your child</p>
                    <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 max-w-md">
                      <p className="mb-2">💡 <strong>Tips:</strong></p>
                      <ul className="text-left space-y-1">
                        <li>• Each conversation is specific to one of your children</li>
                        <li>• Use the search bar to quickly find teachers</li>
                        <li>• Messages are saved and can be viewed anytime</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Reports View ──
  const ReportsView = () => {
    const [reports, setReports] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const currentChild = students[selectedChildIdx] || students[0];

    React.useEffect(() => {
      if (!currentChild?.school_id) { setLoading(false); return; }
      axios.get(buildApiUrl(`/api/student-reports/${currentChild.school_id}`))
        .then(res => {
          if (res.data.success) setReports(res.data.reports || []);
          else setError('Could not load reports');
        })
        .catch(() => setError('Could not load reports'))
        .finally(() => setLoading(false));
    }, [currentChild?.school_id]);

    const formatDate = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatSize = (bytes) => {
      if (!bytes) return '';
      return bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
    };

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0f2752] to-[#1a3a72] px-4 py-4 shadow-lg">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentView('home')} className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-2xl">📊</span>
            <div>
              <h2 className="text-lg font-bold text-white">Reports</h2>
              {currentChild && <p className="text-xs text-blue-200">{currentChild.full_name}</p>}
            </div>
          </div>
        </div>

        {/* Child selector if multiple children */}
        {students.length > 1 && (
          <div className="px-4 pt-4 flex gap-2 overflow-x-auto">
            {students.map((s, i) => (
              <button key={s.school_id} onClick={() => setSelectedChildIdx(i)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedChildIdx === i ? 'bg-[#0f2752] text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}>
                {s.full_name?.split(' ')[0] || s.school_id}
              </button>
            ))}
          </div>
        )}

        <div className="px-4 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          {!loading && !error && reports.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
              <p className="text-4xl mb-3">📂</p>
              <p className="font-semibold text-gray-700">No reports yet</p>
              <p className="text-sm text-gray-400 mt-1">Reports will appear here when your school uploads them.</p>
            </div>
          )}
          {!loading && reports.map(r => {
            const typeLabels = { baseline: 'Baseline', midyear: 'Mid-Year', endofyear: 'End of Year' };
            const typeColors = { baseline: 'bg-blue-100 text-blue-700', midyear: 'bg-amber-100 text-amber-700', endofyear: 'bg-green-100 text-green-700' };
            const typeLabel = typeLabels[r.report_type] || r.report_type;
            const typeColor = typeColors[r.report_type] || 'bg-gray-100 text-gray-600';
            return (
              <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">📄</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-sm truncate">{r.label || r.file_name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {r.year && <span className="text-xs text-gray-400">{r.year}</span>}
                    {r.report_type && <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${typeColor}`}>{typeLabel}</span>}
                    {!r.year && !r.report_type && <span className="text-xs text-gray-400">{formatDate(r.uploaded_at)}</span>}
                  </div>
                </div>
                <a
                  href={buildApiUrl(`/api/student-reports/download/${r.id}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 px-3 py-1.5 bg-[#0f2752] text-white rounded-lg text-xs font-medium hover:bg-[#1a3a72] transition-colors"
                >
                  Open
                </a>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Documents & Address View ──
  const DocumentsView = () => {
    const currentChild = students[selectedChildIdx] || students[0];

    // Address state
    const [address, setAddress] = React.useState({ zone: '', street: '', building: '', unit: '' });
    const [addressEditing, setAddressEditing] = React.useState(false);
    const [addressDraft, setAddressDraft] = React.useState({ zone: '', street: '', building: '', unit: '' });
    const [addressSaving, setAddressSaving] = React.useState(false);
    const [addressMsg, setAddressMsg] = React.useState(null);

    // Documents state
    const [allDocs, setAllDocs] = React.useState([]);
    const [docsLoading, setDocsLoading] = React.useState(true);
    const [uploadingType, setUploadingType] = React.useState(null);
    const [expiryEditing, setExpiryEditing] = React.useState({}); // {doc_type: 'YYYY-MM-DD'}
    const [detectingExpiry, setDetectingExpiry] = React.useState(null); // doc_type currently being detected

    // Parent documents are shared across all siblings
    const PARENT_DOC_TYPES = new Set(['father_passport','mother_passport','father_qid','mother_qid']);

    const REQUIRED_DOCS = [
      { type: 'pupil_passport',    label: 'Pupil Passport',      hasExpiry: true },
      { type: 'father_passport',   label: 'Father Passport',     hasExpiry: true },
      { type: 'mother_passport',   label: 'Mother Passport',     hasExpiry: true },
      { type: 'pupil_qid',         label: 'Pupil QID',           hasExpiry: true },
      { type: '_group_parents_qid', label: 'Parents QID',        isGroupHeader: true },
      { type: 'father_qid',        label: 'Father QID',          hasExpiry: true },
      { type: 'mother_qid',        label: 'Mother QID',          hasExpiry: true },
      { type: 'pupil_immunization',label: 'Pupil Immunization',  hasExpiry: false },
      { type: 'eyfs_form',         label: 'EYFS Form',           hasExpiry: false },
    ];

    // Load address
    React.useEffect(() => {
      if (!parentId) return;
      axios.get(buildApiUrl(`/api/parent/address/${encodeURIComponent(parentId)}`))
        .then(res => {
          if (res.data.success && res.data.address) {
            setAddress(res.data.address);
            setAddressDraft(res.data.address);
          }
        }).catch(() => {});
    }, []);

    // Load documents for ALL siblings so parent docs are shared
    React.useEffect(() => {
      if (!students.length) { setDocsLoading(false); return; }
      setDocsLoading(true);
      Promise.all(
        students.map(s =>
          axios.get(buildApiUrl(`/api/parent/documents/${s.school_id}`))
            .then(res => res.data.success ? (res.data.documents || []) : [])
            .catch(() => [])
        )
      ).then(results => setAllDocs(results.flat()))
       .finally(() => setDocsLoading(false));
    }, [students.length]);

    const saveAddress = async () => {
      setAddressSaving(true); setAddressMsg(null);
      try {
        await axios.post(buildApiUrl(`/api/parent/address/${encodeURIComponent(parentId)}`), addressDraft);
        setAddress(addressDraft);
        setAddressEditing(false);
        setAddressMsg({ ok: true, text: 'Address saved' });
      } catch {
        setAddressMsg({ ok: false, text: 'Failed to save address' });
      } finally {
        setAddressSaving(false);
        setTimeout(() => setAddressMsg(null), 3000);
      }
    };

    const getDoc = (type) => {
      if (PARENT_DOC_TYPES.has(type)) {
        return allDocs.find(d => d.doc_type === type);
      }
      return allDocs.find(d => d.doc_type === type && d.school_id === currentChild?.school_id);
    };

    const DOCS_WITH_EXPIRY = new Set(['pupil_passport','father_passport','mother_passport','pupil_qid','father_qid','mother_qid']);

    const handleUpload = async (docType, file) => {
      if (!file || !currentChild?.school_id) return;
      setUploadingType(docType);
      // Show detecting indicator for image files with expiry fields
      const isImage = file.type.startsWith('image/');
      if (DOCS_WITH_EXPIRY.has(docType) && isImage && !expiryEditing[docType]) {
        setDetectingExpiry(docType);
      }
      const form = new FormData();
      form.append('file', file);
      form.append('school_id', currentChild.school_id);
      form.append('doc_type', docType);
      const expiry = expiryEditing[docType] || '';
      form.append('expiry_date', expiry);
      try {
        const res = await axios.post(buildApiUrl('/api/parent/documents/upload'), form);
        if (res.data.success) {
          setAllDocs(prev => {
            const filtered = prev.filter(d => !(
              d.doc_type === docType &&
              (PARENT_DOC_TYPES.has(docType) || d.school_id === currentChild.school_id)
            ));
            return [...filtered, res.data.document];
          });
          // Auto-fill detected expiry date
          if (res.data.detected_expiry) {
            setExpiryEditing(prev => ({ ...prev, [docType]: res.data.detected_expiry }));
          }
        }
      } catch {}
      finally {
        setUploadingType(null);
        setDetectingExpiry(null);
      }
    };

    const handleExpiryChange = async (docId, docType, value) => {
      setExpiryEditing(prev => ({ ...prev, [docType]: value }));
      if (docId) {
        try {
          await axios.put(buildApiUrl(`/api/parent/documents/${docId}/expiry`), { expiry_date: value });
          setAllDocs(prev => prev.map(d => d.id === docId ? { ...d, expiry_date: value } : d));
        } catch {}
      }
    };

    const formatExpiry = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const activeDocs = REQUIRED_DOCS.filter(d => !d.isGroupHeader);
    const receivedCount = activeDocs.filter(d => getDoc(d.type)).length;

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0f2752] to-[#1a3a72] px-4 py-4 shadow-lg">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentView('home')} className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-2xl">📁</span>
            <div>
              <h2 className="text-lg font-bold text-white">Documents</h2>
              <p className="text-xs text-blue-200">Address &amp; Required Documents</p>
            </div>

          </div>
        </div>

        {/* Child selector */}
        {students.length > 1 && (
          <div className="px-4 pt-4 flex gap-2 overflow-x-auto pb-1">
            {students.map((s, i) => (
              <button key={s.school_id} onClick={() => setSelectedChildIdx(i)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedChildIdx === i ? 'bg-[#0f2752] text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}>
                {s.full_name?.split(' ')[0] || s.school_id}
              </button>
            ))}
          </div>
        )}

        <div className="px-4 py-4 space-y-4 max-w-2xl mx-auto">

          {/* ── Home Address Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">🏠</span>
                <h3 className="font-bold text-gray-800 text-base">Home Address</h3>
              </div>
              {!addressEditing && (
                <button onClick={() => { setAddressDraft({ ...address }); setAddressEditing(true); }}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-lg px-3 py-1 hover:bg-indigo-50 transition-colors">
                  Edit
                </button>
              )}
            </div>
            <div className="px-5 pb-5">
              {addressMsg && (
                <div className={`mb-3 text-xs font-medium px-3 py-2 rounded-lg ${addressMsg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  {addressMsg.text}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'zone', label: 'Zone' },
                  { key: 'street', label: 'Street' },
                  { key: 'building', label: 'Building #' },
                  { key: 'unit', label: 'Unit #' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 mb-1">{label}</label>
                    {addressEditing ? (
                      <input
                        value={addressDraft[key] || ''}
                        onChange={e => setAddressDraft(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 font-medium"
                        placeholder={label}
                      />
                    ) : (
                      <div className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 font-medium min-h-[38px]">
                        {address[key] || <span className="text-gray-300">—</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {addressEditing && (
                <div className="flex gap-2 mt-4">
                  <button onClick={saveAddress} disabled={addressSaving}
                    className="px-5 py-2 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                    {addressSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => { setAddressEditing(false); setAddressDraft({ ...address }); }}
                    className="px-5 py-2 text-gray-600 rounded-xl font-medium text-sm border border-gray-200 hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Student Photos Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 text-center">
              {students.length > 1 ? 'Select Child' : 'My Child'}
            </h3>
            <div className="flex gap-4 justify-center flex-wrap">
              {students.map((s, i) => (
                <div key={s.school_id}
                  className={`flex flex-col items-center p-2 rounded-xl border-2 transition-colors ${students.length > 1 ? (selectedChildIdx === i ? 'border-orange-400 bg-orange-50 cursor-pointer' : 'border-gray-200 hover:border-gray-300 cursor-pointer') : 'border-gray-200'}`}
                  onClick={() => students.length > 1 && setSelectedChildIdx(i)}
                  style={{ minWidth: 80 }}
                >
                  <img
                    src={s.photo_url || buildApiUrl(`/student-photo/${s.school_id}`)}
                    alt={s.full_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                    style={{ background: '#e0e7ef' }}
                  />
                  <span className={`text-xs font-semibold mt-1 ${students.length > 1 && selectedChildIdx === i ? 'text-orange-600' : 'text-gray-700'}`}>
                    {s.full_name?.split(' ')[0] || s.school_id}
                  </span>
                  <span className="text-xs text-gray-400 mb-1">{s.form}</span>
                  <a
                    href={`/student/${s.school_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-[10px] font-semibold text-indigo-500 hover:text-indigo-700 hover:underline whitespace-nowrap"
                  >
                    View Profile ↗
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* ── Required Documents Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="font-bold text-gray-800 text-base">Required Documents</h3>
              <span className="text-xs text-gray-500 font-medium">{receivedCount} / {activeDocs.length} received</span>
            </div>

            {docsLoading && (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!docsLoading && (
              <div className="divide-y divide-gray-50">
                {REQUIRED_DOCS.map((def) => {
                  if (def.isGroupHeader) {
                    return (
                      <div key={def.type} className="flex items-center gap-2 px-5 py-2 bg-gray-50 border-t border-gray-100">
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{def.label}</span>
                      </div>
                    );
                  }

                  const uploaded = getDoc(def.type);
                  const isReceived = !!uploaded;
                  const isExpired = isReceived && uploaded?.expiry_date && new Date(uploaded.expiry_date) < new Date();
                  const isUploading = uploadingType === def.type;
                  const inputId = `doc-upload-${def.type}`;
                  const expiryVal = expiryEditing[def.type] !== undefined
                    ? expiryEditing[def.type]
                    : (uploaded?.expiry_date || '');

                  return (
                    <div key={def.type}
                      className={`flex items-center gap-3 px-5 py-3.5 ${isExpired ? 'bg-red-50/40' : isReceived ? 'bg-green-50/50' : ''}`}>
                      {/* Checkbox */}
                      <div className={`w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center border-2 ${
                        isExpired ? 'bg-red-400 border-red-400' : isReceived ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'
                      }`}>
                        {isReceived && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>

                      {/* Label + expiry */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{def.label}</p>
                        {def.hasExpiry && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-xs text-gray-400">Expiry:</span>
                            {detectingExpiry === def.type ? (
                              <span className="flex items-center gap-1 text-xs text-indigo-500 font-medium">
                                <span className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin inline-block" />
                                Detecting…
                              </span>
                            ) : (
                              <input
                                type="date"
                                value={expiryVal}
                                onChange={e => handleExpiryChange(uploaded?.id || null, def.type, e.target.value)}
                                className={`text-xs border-0 bg-transparent font-medium focus:outline-none focus:ring-1 focus:ring-indigo-300 rounded px-1 py-0 w-32 ${
                                  expiryVal && new Date(expiryVal) < new Date()
                                    ? 'text-red-500'
                                    : 'text-gray-600'
                                }`}
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Status badges */}
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`text-xs font-semibold whitespace-nowrap ${isExpired ? 'text-red-500' : isReceived ? 'text-green-600' : 'text-gray-400'}`}>
                          {isExpired ? 'Upload Again' : isReceived ? 'Received' : 'Pending'}
                        </span>

                      </div>

                      {/* Upload button */}
                      <label htmlFor={inputId}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors border ${
                          isUploading
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}>
                        {isUploading ? (
                          <span className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        )}
                        {isUploading ? 'Uploading…' : 'Upload'}
                      </label>
                      <input
                        id={inputId}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={e => {
                          if (e.target.files[0]) handleUpload(def.type, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main render logic
  const renderCurrentView = () => {
    if (currentView === "home") return <HomePage />;
    if (currentView === "resources") return <ResourcesView />;
    if (currentView === "notes") return <NotesView />;
    if (currentView === "chat") return <ChatView />;
    if (currentView === "ai-assistant") return <AIAssistantView />;
    if (currentView === "assignments") return <AssignmentsView />;
    if (currentView === "posts") {
      const postsCurrentChild = students[postsChildIdx];
      return (
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-white/10 backdrop-blur-md shadow-lg border-b border-white/20 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                {/* Left: Hamburger */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="p-2 text-gray-700 hover:bg-white/30 rounded-lg transition-colors"
                    title="Filters"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>

                {/* Center: Child Selector */}
                <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
                  <CompactChildSelector
                    onSelectChild={setPostsChildIdx}
                    currentChildId={postsCurrentChild?.school_id}
                  />
                </div>

                {/* Right: Home + Notification */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToHome()}
                    className="p-2 text-gray-700 hover:bg-white/30 rounded-lg transition-all"
                    title="Home"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </button>
                  <button
                    onClick={openNotifPanel}
                    className="p-2 text-gray-700 hover:bg-white/30 rounded-lg transition-all relative"
                    title="Notifications"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {notifUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
                        {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
              <SchoolPostsTab
                studentForm={postsCurrentChild?.form}
                studentId={postsCurrentChild?.school_id}
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
              />
            </div>
          </div>
        </div>
      );
    }
    if (currentView === "parents-evening") return <ParentsEveningSystem key={currentView} userRole="parent" userEmail={parentId} userName={parentName} students={students} onBack={() => setCurrentView('home')} />;
    if (currentView === "club-booking") return <ParentClubBooking parentEmail={parentId} parentName={parentName} students={students} onBack={() => setCurrentView('home')} />;
    if (currentView === "consent-forms") return <ParentConsentForms parentEmail={parentId} parentName={parentName} students={students} onBack={() => setCurrentView('home')} />;
    if (currentView === "school-policies") return <ParentSchoolPolicies onBack={() => setCurrentView('home')} />;
    if (currentView === "timetable") return <ParentTimetable students={students} onBack={() => setCurrentView('home')} />;
    if (currentView === "reports") return <ReportsView />;
    if (currentView === "documents") return <DocumentsView />;
    return <HomePage />;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {renderCurrentView()}

      {/* 🔔 Notification Panel Dropdown - rendered globally so it works on all views */}
      {showNotifPanel && (
        <>
          {/* Backdrop to close on outside click */}
          <div className="fixed inset-0 z-[99]" onClick={() => setShowNotifPanel(false)} />
          <div className="fixed top-16 right-4 w-80 max-h-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
              <h3 className="font-semibold text-gray-800">🔔 Notifications</h3>
              <div className="flex items-center gap-2">
                {notifUnreadCount > 0 && (
                  <button
                    onClick={markAllNotifsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark all read
                  </button>
                )}
                <button onClick={() => setShowNotifPanel(false)} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-80">
              {isLoadingNotifs ? (
                <div className="py-8 text-center text-gray-400">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center text-gray-400">No notifications yet</div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    className={`px-4 py-3 border-b last:border-b-0 cursor-pointer ${!n.is_read ? 'bg-blue-50' : 'bg-white'} hover:bg-gray-100 transition-colors`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{n.title}</p>
                        {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                        <p className="text-xs text-gray-400 mt-1">
                          {n.created_at ? new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                      <svg className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Mobile Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex justify-around items-center h-16">
          {/* Home/Menu Icon */}
          <button
            onClick={() => setCurrentView('home')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'home' ? 'text-blue-600' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="12" width="7" height="7" rx="1" />
              <rect x="12" y="3" width="7" height="7" rx="1" />
              <rect x="12" y="12" width="7" height="7" rx="1" />
            </svg>
            <span className="text-xs mt-1">Menu</span>
          </button>

          {/* Posts Icon */}
          <button
            onClick={() => setCurrentView('posts')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'posts' ? 'text-blue-600' : 'text-gray-600'
            }`}
          >
            <span className="text-2xl">📢</span>
            <span className="text-xs mt-1">Posts</span>
          </button>

          {/* Chat Icon */}
          <button
            onClick={() => setCurrentView('chat')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'chat' ? 'text-blue-600' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-xs mt-1">Chat</span>
          </button>
        </div>
      </div>
    </div>
  );
}
export default ParentDashboard;
